<template>
  <div>
    <v-row dense>
      <v-col
        :xl="2"
        :md="6"
        :cols="12"
      >
        <v-row dense>
          <v-col :cols="6">
            <text-field-date-picker
              v-model="diet.fromDate"
              :label="$t('dietPanelStrings.labels.fromDate')"
              :allowed-dates="
                (x) =>
                  (!diet.toDate ||
                    m(x).isSameOrBefore(diet.toDate, 'days')) &&
                  m(x).isSameOrBefore(m(), 'days')
              "
            />
          </v-col>
          <v-col :cols="6">
            <text-field-time-picker
              v-model="diet.fromDate"
              :label="$t('dietPanelStrings.labels.time')"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col
        :xl="2"
        :md="6"
        :cols="12"
      >
        <v-row dense>
          <v-col :cols="6">
            <text-field-date-picker
              v-model="diet.toDate"
              :label="$t('dietPanelStrings.labels.toDate')"
              :allowed-dates="
                (x) =>
                  diet.fromDate &&
                  m(x).isSameOrAfter(diet.fromDate, 'days') &&
                  m(x).isSameOrBefore(m(), 'days') &&
                  m(x).isSameOrBefore(
                    m(diet.fromDate).add(30, 'd'),
                    'days'
                  )
              "
            />
          </v-col>
          <v-col :cols="6">
            <text-field-time-picker
              v-model="diet.toDate"
              :label="$t('dietPanelStrings.labels.time')"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col
        :cols="12"
        :sm="6"
        :md="3"
        :lg="2"
        :xl="1"
      >
        <v-row dense>
          <v-col>
            <vee-field
              v-slot="{ componentField, errors }"
              v-model="diet.distance"
              rules="required|integer|min_value:15"
              :name="$t('dietPanelStrings.labels.distance')"
            >
              <integer-text-field
                v-bind="componentField"
                :error-messages="errors"
                :label="$t('dietPanelStrings.labels.distance')"
              />
            </vee-field>
          </v-col>
        </v-row>
      </v-col>
      <v-col
        v-if="canUseSalaryFraction"
        :cols="12"
        :sm="6"
        :md="3"
        :lg="2"
        :xl="1"
      >
        <v-row dense>
          <v-col>
            <v-checkbox
              v-model="diet.usesSalaryFraction"
              color="primary"
              :label="$t('commonStrings.salaryFractionLabel')"
              :disabled="!salaryFractionSplitAvailable"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col
        :cols="12"
        :md="canUseSalaryFraction ? 6 : 9"
        :lg="canUseSalaryFraction ? 8 : 10"
        :xl="3"
      >
        <v-row dense>
          <v-col>
            <vee-field
              v-if="!diet.usesSalaryFraction"
              v-slot="{ componentField, errors }"
              v-model="diet.payingOrgs"
              rules="required"
              :name="$t('dietPanelStrings.labels.payingOrgs')"
            >
              <v-select
                multiple
                item-value="id"
                item-title="name"
                v-bind="componentField"
                chips
                closable-chips
                :disabled="payingOrgsIsDisabled"
                :items="availablePayingOrgs"
                :error-messages="errors"
                variant="outlined"
                density="compact"
                :label="$t('dietPanelStrings.labels.payingOrgs')"
                name="payingOrgs"
                :placeholder="$t('dietPanelStrings.placeholders.payingOrgs')"
              >
                <template #append>
                  <v-icon tag="span">
                    mdi-menu-down
                  </v-icon>
                </template>
              </v-select>
            </vee-field>
            <v-tooltip
              v-else
              location="top"
              color="black"
            >
              <template #activator="{ props }">
                <v-chip
                  v-bind="props"
                  class="my-0"
                >
                  {{ $t("dietPanelStrings.splitBySalaryFraction") }}
                  <v-icon
                    tag="span"
                    class="ml-2"
                  >
                    info
                  </v-icon>
                </v-chip>
              </template>
              <ul>
                <li
                  v-for="(parts, orgId) in salaryFractions"
                  :key="orgId"
                  class="mb-1"
                >
                  {{
                    $tc("dietPanelStrings.salaryFractionExplanation", parts, {
                      org: getOrgNameByOrgId(orgId),
                      parts,
                      total: totalFractionsCount,
                    })
                  }}
                </li>
              </ul>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-col>
      <v-col
        :cols="12"
        :xl="3"
      >
        <v-row dense>
          <v-col>
            <v-text-field
              v-model="diet.comment"
              density="compact"
              variant="outlined"
              :label="$t('dietPanelStrings.labels.comment')"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import TextFieldDatePicker from "@/views/common/components/TextFieldDatePicker.vue";
import TextFieldTimePicker from "@/views/common/components/TextFieldTimePicker.vue";
import IntegerTextField from "@/views/common/components/IntegerTextField";
import { Field as VeeField } from "vee-validate";
import { mapGetters } from "vuex";
import { copy } from "@/utils/object";
import { getOrgDisplayName } from "@/utils/org";
import { m } from '@/plugins/moment';

export default {
  name: "DietInformation",
  components: {
    TextFieldDatePicker,
    TextFieldTimePicker,
    VeeField,
    IntegerTextField
  },
  props: {
    diet: {
      type: Object,
      required: true
    },
    canUseSalaryFraction: {
      required: true
    }
  },
  setup() {
    return { m };
  },
  watch: {
    "diet.usesSalaryFraction"(val) {
      if (val) {
        this.diet.payingOrgs = this.salaryFractionOrgIds;
      } else {
        this.diet.payingOrgs = [];
      }
    },
    salaryFractionOrgIds(val) {
      if (this.diet.usesSalaryFraction) {
        if (val) {
          this.diet.payingOrgs = val;
        } else {
          this.diet.payingOrgs = [];
        }
      }
    }
  },
  methods: {
    getOrgNameByOrgId(orgId) {
      orgId = parseInt(orgId, 10);

      const org = this.currentApplicationOrgs.find(x => x.id === orgId);

      if (org != null) {
        return getOrgDisplayName(org);
      }

      return "";
    }
  },
  computed: {
    ...mapGetters({
      salaryFractions: "application/salaryFractionsStore/fractions",
      payingOrgs: "application/payingOrgsStore/payingOrgs",
      getRateConfiguration:
        "application/rateConfigurationsStore/rateConfiguration",
      currentApplicationOrgs: "application/currentApplicationStore/orgs",
      totalFractionsCount:
        "application/salaryFractionsStore/totalFractionsCount",
      salaryFractionSplitAvailable:
        "application/salaryFractionsStore/salaryFractionSplitAvailable",
      salaryFractionOrgIds: "application/salaryFractionsStore/uniqueOrgIds"
    }),
    payingOrgsIsDisabled() {
      return this.diet.usesSalaryFraction;
    },
    availablePayingOrgs() {
      // from date and to date are mandatory
      if (!this.diet?.fromDate || !this.diet?.toDate) {
        return [];
      }

      const range = m.range(this.diet.fromDate, this.diet.toDate);

      // if the duration is more than just a couple of days it is usually a problem
      // adding this check, just to avoid excessive calculations
      if (range.diff("days") > 30) {
        return [];
      }

      const daysInRange = [...range.by("day")];
      const that = this;

      const result = [];
      for (const org of this.payingOrgs) {
        const rateConfigs = daysInRange
          .map(x => that.getRateConfiguration(org.id, x, "dietRates"))
          .filter(x => x);

        if (rateConfigs && rateConfigs.length > 0) {
          const orgCopy = copy(org);

          orgCopy.name = getOrgDisplayName(orgCopy);

          if (rateConfigs.length !== daysInRange.length) {
            // useful information to be displayed in
            // dropdown text, yet it is hard to
            // implement it via slots, so should be researched
            orgCopy.dropdownName = this.$t(
              "dietPanelStrings.notAvailableForCompletePeriod",
              [orgCopy.name]
            );

            orgCopy.name = `${orgCopy.name}`;
          } else {
            orgCopy.dropdownName = orgCopy.name;
          }

          result.push(orgCopy);
        }
      }

      return result;
    }
  }
};
</script>
